import { useState } from 'react'
import type { DemandCostsResult, DemandIssue, DesignCostsResult, VideoCostsResult } from '@/types'
import { DEMAND_ISSUE_LABELS } from '@/utils/demandCosts'
import { getCycleRange } from '@/utils/operations'
import { formatDateBR } from '@/utils/dateParser'
import { formatBRL } from '@/utils/currency'

interface CostOverviewProps {
  data: DemandCostsResult | null
  loading: boolean
  productionMonth: number
  productionYear: number
}

/** Formats a month/year pair as "Janeiro/2026" (first letter capitalised) */
function formatMonthYear(month: number, year: number): string {
  const raw = new Date(year, month, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  })
  // pt-BR returns "janeiro de 2026" → normalise to "Janeiro/2026"
  return raw
    .replace(' de ', '/')
    .replace(/^(.)/, (c) => c.toUpperCase())
}

/**
 * Total pendencies shown in the summary — purely a display count over
 * fields already computed by calculateDemandCosts. No pricing/business
 * logic here, just tallying what's already flagged as incomplete.
 */
function countPendencies(data: DemandCostsResult): number {
  return (
    data.design.issues.length +
    (data.design.error ? 1 : 0) +
    (data.video.error ? 1 : 0) +
    (data.video.totalCost === null && data.video.count > 0 ? 1 : 0)
  )
}

export function CostOverview({
  data,
  loading,
  productionMonth,
  productionYear,
}: CostOverviewProps) {
  const [designExpanded, setDesignExpanded] = useState(false)
  const [videoExpanded, setVideoExpanded] = useState(false)

  const cycleLabel = formatMonthYear(productionMonth, productionYear)
  const { start: cycleStart, end: cycleEnd } = getCycleRange(productionMonth, productionYear)

  return (
    <section>
      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mb-4">
        <h2 className="text-xs font-semibold text-brand-taupe uppercase tracking-wider shrink-0">
          Custos de Produção
        </h2>
        <div className="hidden sm:block flex-1 h-px bg-brand-beige" />
        {!loading && (
          <p className="text-xs text-brand-taupe">
            Ciclo de pagamento de{' '}
            <span className="font-medium text-brand-black">{formatDateBR(cycleStart)}</span>
            {' '}a{' '}
            <span className="font-medium text-brand-black">{formatDateBR(cycleEnd)}</span>
            {' '}— referente aos conteúdos de{' '}
            <span className="font-medium text-brand-black">{cycleLabel}</span>
          </p>
        )}
      </div>

      {loading || !data ? (
        <SkeletonBlock />
      ) : data.error ? (
        <div className="card p-6 text-center text-red-500 text-sm">{data.error}</div>
      ) : (
        <div className="space-y-4">
          {/* Compact summary row — 3 up on desktop, 2+1 on tablet, stacked on mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <DesignSummaryCard
              design={data.design}
              expanded={designExpanded}
              onToggle={() => setDesignExpanded((v) => !v)}
            />
            <VideoSummaryCard
              video={data.video}
              expanded={videoExpanded}
              onToggle={() => setVideoExpanded((v) => !v)}
            />
            <TotalCostCard
              design={data.design}
              video={data.video}
              knownTotalCost={data.knownTotalCost}
              hasPendingCosts={data.hasPendingCosts}
              pendingCount={countPendencies(data)}
            />
          </div>

          {/* Detail panels — full width, only when opened, never inline in the card */}
          {designExpanded && !data.design.error && <DesignDetailPanel design={data.design} />}
          {videoExpanded && !data.video.error && <VideoDetailPanel video={data.video} />}
        </div>
      )}
    </section>
  )
}

// ─── Shared bits ──────────────────────────────────────────────────────────────

function DetailToggle({ expanded, onClick }: { expanded: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className="btn-ghost text-xs px-2.5 py-1 shrink-0">
      {expanded ? 'Ocultar detalhamento' : 'Ver detalhamento'}
    </button>
  )
}

function IssuesList({ issues }: { issues: DemandIssue[] }) {
  return (
    <div className="mt-4 pt-4 border-t border-brand-beige">
      <p className="text-xs font-semibold text-brand-taupe uppercase tracking-wider mb-2">
        Pendências ({issues.length})
      </p>
      <ul className="space-y-1.5">
        {issues.map((issue) => (
          <li key={issue.cardId} className="flex items-center justify-between gap-3 text-xs">
            <a
              href={issue.cardShortUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-black hover:text-brand-taupe truncate underline decoration-brand-beige underline-offset-2"
              title={issue.detail}
            >
              {issue.cardName}
            </a>
            <span className="badge bg-yellow-50 text-yellow-700 border border-yellow-200 shrink-0">
              {DEMAND_ISSUE_LABELS[issue.kind]}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ─── Design summary card (compact) ─────────────────────────────────────────

function DesignSummaryCard({
  design,
  expanded,
  onToggle,
}: {
  design: DesignCostsResult
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-brand-beige-light flex items-center justify-center text-brand-taupe shrink-0">
            <DesignIcon />
          </div>
          <span className="font-semibold text-brand-black text-sm truncate">Design</span>
        </div>
        {!design.error && <DetailToggle expanded={expanded} onClick={onToggle} />}
      </div>

      {design.error ? (
        <p className="text-xs text-red-500">{design.error}</p>
      ) : (
        <>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-semibold text-brand-black">{design.totalCount}</span>
            <span className="text-xs text-brand-taupe">
              demanda{design.totalCount !== 1 ? 's' : ''}
            </span>
          </div>
          <p className="text-base font-medium text-brand-black mt-0.5">
            {formatBRL(design.totalCost)}
          </p>
        </>
      )}
    </div>
  )
}

// ─── Design detail panel (full width, below the summary row) ──────────────

function DesignDetailPanel({ design }: { design: DesignCostsResult }) {
  return (
    <div className="card p-5">
      <div className="divide-y divide-brand-beige">
        {design.categories.map((cat) => (
          <div key={cat.key} className="flex items-center justify-between gap-3 py-2.5 text-sm">
            <span className="text-brand-black">{cat.label}</span>
            <div className="flex items-center gap-4 shrink-0">
              <span className="text-brand-taupe text-xs whitespace-nowrap">
                {cat.count} × {formatBRL(cat.unitPrice)}
              </span>
              <span className="font-medium text-brand-black w-20 text-right">
                {formatBRL(cat.cost)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-3 mt-1 border-t border-brand-beige">
        <span className="text-xs font-semibold text-brand-taupe uppercase tracking-wider">
          Total Design
        </span>
        <span className="text-lg font-semibold text-brand-black">
          {formatBRL(design.totalCost)}
        </span>
      </div>

      {design.issues.length > 0 && <IssuesList issues={design.issues} />}
    </div>
  )
}

// ─── Video summary card (compact) ──────────────────────────────────────────

function VideoSummaryCard({
  video,
  expanded,
  onToggle,
}: {
  video: VideoCostsResult
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-brand-beige-light flex items-center justify-center text-brand-taupe shrink-0">
            <VideoIcon />
          </div>
          <span className="font-semibold text-brand-black text-sm truncate">Vídeo</span>
        </div>
        {!video.error && <DetailToggle expanded={expanded} onClick={onToggle} />}
      </div>

      {video.error ? (
        <p className="text-xs text-red-500">{video.error}</p>
      ) : (
        <>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-semibold text-brand-black">{video.count}</span>
            <span className="text-xs text-brand-taupe">
              demanda{video.count !== 1 ? 's' : ''}
            </span>
          </div>
          {video.totalCost !== null ? (
            <p className="text-base font-medium text-brand-black mt-0.5">
              {formatBRL(video.totalCost)}
            </p>
          ) : (
            <span className="badge bg-yellow-50 text-yellow-700 border border-yellow-200 text-xs mt-1 inline-block">
              Valor avulso não configurado
            </span>
          )}
        </>
      )}
    </div>
  )
}

// ─── Video detail panel (full width, below the summary row) ───────────────

function VideoDetailPanel({ video }: { video: VideoCostsResult }) {
  return (
    <div className="card p-5 space-y-2 text-sm">
      <div className="flex items-center justify-between">
        <span className="text-brand-taupe">Quantidade</span>
        <span className="text-brand-black">
          {video.count} vídeo{video.count !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-brand-taupe">Regra aplicada</span>
        <span className="text-brand-black text-right">
          {video.meetsPackageMinimum
            ? `≥ ${video.packageMinimum} vídeos — pacote`
            : `< ${video.packageMinimum} vídeos — avulso`}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-brand-taupe">Valor unitário</span>
        <span className="text-brand-black">
          {video.unitPrice !== null ? formatBRL(video.unitPrice) : 'Não configurado'}
        </span>
      </div>
      <div className="flex items-center justify-between pt-2 mt-1 border-t border-brand-beige">
        <span className="text-xs font-semibold text-brand-taupe uppercase tracking-wider">
          Total Vídeo
        </span>
        <span className="text-lg font-semibold text-brand-black">
          {video.totalCost !== null ? formatBRL(video.totalCost) : 'Pendente'}
        </span>
      </div>

      {video.totalCost === null && (
        <div className="pt-3 mt-1 border-t border-brand-beige">
          <p className="text-xs font-semibold text-brand-taupe uppercase tracking-wider mb-2">
            Pendências (1)
          </p>
          <span className="badge bg-yellow-50 text-yellow-700 border border-yellow-200 text-xs">
            Valor avulso não configurado
          </span>
        </div>
      )}
    </div>
  )
}

// ─── Total card (compact — Design/Vídeo shown secondary, total is priority) ─

function TotalCostCard({
  design,
  video,
  knownTotalCost,
  hasPendingCosts,
  pendingCount,
}: {
  design: DesignCostsResult
  video: VideoCostsResult
  knownTotalCost: number
  hasPendingCosts: boolean
  pendingCount: number
}) {
  return (
    <div className="card p-4 sm:col-span-2 lg:col-span-1">
      <span className="text-xs font-semibold text-brand-taupe uppercase tracking-wider">
        Total de Produção
      </span>

      <div className="space-y-1 text-xs mt-2 mb-3">
        <div className="flex items-center justify-between">
          <span className="text-brand-taupe">Design</span>
          <span className="text-brand-black font-medium">{formatBRL(design.totalCost)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-brand-taupe">Vídeo</span>
          <span className="text-brand-black font-medium">
            {video.totalCost !== null ? formatBRL(video.totalCost) : 'Pendente'}
          </span>
        </div>
      </div>

      <div className="pt-2 border-t border-brand-beige">
        <p className="text-xs font-medium text-brand-black">
          {hasPendingCosts ? 'Total parcial conhecido' : 'Total'}
        </p>
        <p className="text-xl font-semibold text-brand-black mt-0.5">
          {formatBRL(knownTotalCost)}
        </p>
      </div>

      {hasPendingCosts && (
        <p className="mt-2">
          <span className="badge bg-yellow-50 text-yellow-700 border border-yellow-200 text-xs">
            {pendingCount === 1 ? '1 pendência' : `${pendingCount} pendências`}
          </span>
        </p>
      )}
    </div>
  )
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonBlock() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="card p-4 animate-pulse">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-brand-beige rounded-xl" />
            <div className="h-4 bg-brand-beige rounded w-16" />
          </div>
          <div className="h-6 bg-brand-beige rounded w-20 mb-2" />
          <div className="h-4 bg-brand-beige rounded w-24" />
        </div>
      ))}
    </div>
  )
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function DesignIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l9 4.5v11L12 22 3 17.5v-11L12 2z" />
      <path d="M12 2v20" />
      <path d="M3 6.5l9 5 9-5" />
    </svg>
  )
}

function VideoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="6" width="14" height="12" rx="2" />
      <path d="M16 10l6-3v10l-6-3V10z" />
    </svg>
  )
}
